import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import { isEqual } from 'lodash';
import FormAccordionBase from '@/components/forms/new/FormAccordionBase';
import { PartnerFormProvider } from './context/PartnerFormContext';
import { usePartnerForm } from './hooks/usePartnerForm.js';
import walkthroughIds from '../../walkthroughIds.js';
import { LoadingIndicator } from '@/components/v4';
import getPlans from '@/API/getPlans';
import { axios } from '@/services/axios';

import PartnerGeneral from './tabs/PartnerGeneral';
import PartnerBillingInvoice from './tabs/PartnerBillingInvoice';
import PartnerCustomAttributes from './tabs/PartnerCustomAttributes';
import PartnerAdmin from './tabs/PartnerAdmin';
import useCreatePartner from './queries/useCreatePartner';
import useGetPartnerByID from './queries/useGetPartnerByID';
import useUpdatePartner from './queries/useUpdatePartner';
import { getInitialFormData } from './utils/partnerFormUtils.js';

const PartnerEditContent = ({
    initialData,
    mode,
    resetToMainTable,
    switchToEditMode,
    selectedPartnerId,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { partners: partnerWalkthroughIds } = walkthroughIds;
    const dispatch = useDispatch();

    const {
        formData,
        setFormData,
        setMode,
        initialFormData,
        setInitialFormData,
        deletedBillingNoteIDs,
    } = usePartnerForm();

    const isAddMode = mode === 'Add';
    const isEditMode = mode === 'Edit';

    // Wrapper for resetToMainTable that dispatches Redux action
    const handleBackClick = () => {
        dispatch({ type: 'CHANGE_TABLE_LOADING' });
        resetToMainTable();
    };

    useEffect(() => {
        setMode(mode);
    }, [mode, setMode]);

    useEffect(() => {
        if (isAddMode) {
            setFormData(getInitialFormData(initialData));
            setInitialFormData(getInitialFormData(initialData));
        }
    }, [isAddMode, setFormData, initialData, setInitialFormData]);

    const { isLoading, isFetching } = useGetPartnerByID(
        selectedPartnerId,
        (data) => {
            setFormData(data);
            setInitialFormData(data);
        },
        resetToMainTable,
        isEditMode,
    );

    const getInitialValues = () => {
        return formData;
    };

    const hasFormErrors = (errors) => {
        if (!errors || typeof errors !== 'object') return false;

        const checkNestedErrors = (obj) => {
            return Object.values(obj).some((value) => {
                if (typeof value === 'object' && value !== null) {
                    return checkNestedErrors(value);
                }
                return Boolean(value);
            });
        };

        return checkNestedErrors(errors);
    };

    const hasFormChanged = (currentValues) => {
        if (!initialFormData || !currentValues?.formValues) return true;
        return !isEqual(initialFormData, currentValues?.formValues);
    };

    const calculateIsSubmitDisabled = (errors, values) => {
        const hasErrors = hasFormErrors(errors);
        const hasChanged = hasFormChanged(values);
        return hasErrors || !hasChanged;
    };

    const breadCrumbTrail = [
        { label: 'Partners', path: resetToMainTable },
        {
            label: isAddMode ? 'Add Partner' : 'Edit Partner',
            path: '',
        },
    ];

    const header = isAddMode
        ? 'Add Partner'
        : `Edit ${formData?.name || 'Partner'}`;

    const { mutate: createPartner } = useCreatePartner(
        formData,
        (createdPartner) => {
            // After creating, switch to edit mode with the new partner ID
            if (switchToEditMode && createdPartner?.id) {
                switchToEditMode(createdPartner.id);
            } else {
                resetToMainTable();
            }
        },
    );
    const { mutate: updatePartner } = useUpdatePartner(
        formData,
        resetToMainTable,
        deletedBillingNoteIDs,
        initialFormData,
    );

    // For partner default plans, we need to fetch partner-level plans
    // When editing a partner, formData.id IS the partner ID we need
    const [additionalPlans, setAdditionalPlans] = useState([]);
    const { currentWholesaler } = useSelector((state) => state.navigationLists);

    // Get partner plans for the dropdown
    // In Add mode: fetch wholesaler plans (currentWholesaler)
    // In Edit mode: fetch partner's available plans (formData.id)
    const plansFetchId = isEditMode ? formData?.id : currentWholesaler;

    const { isLoading: isDefaultPlansLoading } = useQuery({
        queryKey: ['partnerDefaultPlans', plansFetchId, isAddMode],
        queryFn: async () => {
            if (!plansFetchId) {
                console.warn('plansFetchId is missing, cannot fetch plans');
                return [];
            }

            try {
                let plans;
                // In Add mode, fetch wholesaler plans using the old API endpoint
                if (isAddMode) {
                    const response = await axios.get(
                        `/wholesaler/${plansFetchId}/plans`,
                        {
                            params: {
                                wholesalerOnly: true,
                            },
                        },
                    );
                    plans = response.data;
                } else {
                    // In Edit mode, fetch partner's available plans
                    plans = await getPlans({ partnerId: plansFetchId });
                }

                setAdditionalPlans(Array.isArray(plans) ? plans : []);
                return plans || [];
            } catch (error) {
                console.error('Failed to fetch partner plans:', error);
                setAdditionalPlans([]);
                return [];
            }
        },
        enabled: !!plansFetchId,
        refetchOnWindowFocus: false,
        refetchOnMount: true, // Always refetch when component mounts
        staleTime: 0, // Don't use stale data, always refetch
        cacheTime: 0, // Don't cache the results
    });

    // Build the complete plans list:
    // In Add mode: just use all additionalPlans from wholesaler
    // In Edit mode: include current defaultPartnerPlan + additional partner plans
    const buildDefaultPlansList = () => {
        // In Add mode, just return all wholesaler plans
        if (isAddMode) {
            return additionalPlans || [];
        }

        // In Edit mode, build list with current plan + additional plans
        const plansList = [];
        const planIds = new Set();

        // Always include the current defaultPartnerPlan if it exists
        if (formData?.defaultPartnerPlan) {
            plansList.push(formData.defaultPartnerPlan);
            planIds.add(formData.defaultPartnerPlan.id);
        }

        // Add any additional plans that aren't duplicates
        if (additionalPlans?.length > 0) {
            additionalPlans.forEach((plan) => {
                if (plan && !planIds.has(plan.id)) {
                    plansList.push(plan);
                    planIds.add(plan.id);
                }
            });
        }

        return plansList;
    };

    const effectivePlans = buildDefaultPlansList();

    const defaultPlansData = {
        defaultPlans: effectivePlans,
        isDefaultPlansLoading,
    };

    const buildTabs = (errors, touched, values) => {
        const tabs = [
            {
                label: 'General',
                accordions: (
                    <PartnerGeneral
                        key="partner-general"
                        darkMode={darkMode}
                        errors={errors}
                        touched={touched}
                        values={values}
                        isAddMode={isAddMode}
                        defaultPlansData={defaultPlansData}
                    />
                ),
            },
        ];

        // Add additional tabs for edit mode
        if (!isAddMode) {
            tabs.push(
                {
                    label: 'Billing & Invoice',
                    accordions: (
                        <PartnerBillingInvoice
                            key="partner-billing"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            defaultPlansData={defaultPlansData}
                        />
                    ),
                },
                {
                    label: 'Custom Fields',
                    accordions: [
                        <PartnerCustomAttributes
                            key="partner-customAttributes"
                            darkMode={darkMode}
                            errors={errors}
                        />,
                    ],
                },
                {
                    label: 'Admin',
                    accordions: [
                        <PartnerAdmin
                            key="partner-admin"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            resetToMainTable={resetToMainTable}
                        />,
                    ],
                },
            );
        }

        return tabs;
    };

    if (((isLoading || isFetching) && isEditMode) || isDefaultPlansLoading) {
        return <LoadingIndicator />;
    }

    const finalFormSubmit = (values) => {
        // Sync Formik values back to formData before submission
        const submitData = values?.formValues || formData;
        // Update formData with the latest Formik values
        setFormData(submitData);
        // Wait a tick for state to update, then submit
        setTimeout(() => {
            if (isAddMode) {
                createPartner();
            } else {
                updatePartner();
            }
        }, 0);
    };

    return (
        <>
            <FormAccordionBase
                breadCrumbTrail={breadCrumbTrail}
                header={header}
                initialValues={getInitialValues()}
                finalFormSubmit={finalFormSubmit}
                backLink={handleBackClick}
                submitButtonText={isAddMode ? 'Create' : 'Save'}
                ufBackBtn={partnerWalkthroughIds.ufBackBtn}
                ufSaveBtn={partnerWalkthroughIds.ufSaveBtn}
                enableReinitialize={true}
                enableTabs={true}
                isSubmitDisabled={calculateIsSubmitDisabled || true}
                defaultTabIndex={0}>
                {({ errors, touched, values }) =>
                    buildTabs(errors, touched, values)
                }
            </FormAccordionBase>
        </>
    );
};

const PartnerEdit = ({
    mode,
    resetToMainTable,
    switchToEditMode,
    selectedPartnerId,
}) => {
    const { currentWholesaler } = useSelector((state) => state.navigationLists);
    const initialData = {
        wholesalerID: currentWholesaler,
        enabled: true,
        trunkLocationID: 'MEL',
    };

    return (
        <PartnerFormProvider initialData={initialData}>
            <PartnerEditContent
                mode={mode}
                resetToMainTable={resetToMainTable}
                switchToEditMode={switchToEditMode}
                selectedPartnerId={selectedPartnerId}
                initialData={initialData}
            />
        </PartnerFormProvider>
    );
};

export default PartnerEdit;
